/* eslint-disable jsx-a11y/media-has-caption */
/**
 * © 2024 Little Shilling, Inc.
 * Shon Little
 * Created: 2024-04-17
 */

// Add third-party dependencies.
import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Alert, useMediaQuery, useTheme } from '@mui/material';

/**
 * VideoPlayer component.
 * @example
 * return (
 *   <VideoPlayer videoFiles={videoFiles} responsive />
 * )
 * @returns {React.ReactElement} component.
 */
const VideoPlayer = ({ videoFiles, responsive }) => {
  // Set Ref hook.
  const player = useRef(null);

  // Set state hook.
  const [errorMessage, setErrorMessage] = useState('');

  // Set theme and media query hooks.
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Event handler for video errors
  const handleError = () => {
    const { error } = player.current;
    if (error) {
      console.error('error', error);
      switch (error.code) {
        case error.MEDIA_ERR_ABORTED:
          setErrorMessage('Video playback was aborted.');
          break;
        case error.MEDIA_ERR_NETWORK:
          setErrorMessage('A network error caused the video download to fail.');
          break;
        case error.MEDIA_ERR_DECODE:
          setErrorMessage(
            'The video playback was aborted due to a corruption problem or because the video used features your browser did not support.'
          );
          break;
        case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
          setErrorMessage(
            'The video could not be loaded, either because the server or network failed or because the format is not supported.'
          );
          break;
        default:
          setErrorMessage('An unknown error occurred.');
          break;
      }
    }
  };

  // Video sources.
  const sources = videoFiles.map((file) => <source key={file.id} src={encodeURI(file.src)} type={file.type} />);

  // Determine max height based on responsive prop and device
  let maxHeight = '400px';
  if (responsive) {
    maxHeight = '100%';
  } else if (isMobile) {
    maxHeight = 'none';
  }

  // Render component.
  return (
    <Box>
      {errorMessage && (
        <Alert severity="error" style={{ marginBottom: '20px' }}>
          {errorMessage}
        </Alert>
      )}
      <video
        ref={player}
        controls
        id="video"
        playsInline
        onError={handleError}
        style={{
          width: '100%', // Always use 100% width for better responsiveness
          maxHeight, // Use the pre-determined value
          objectFit: 'contain',
          maxWidth: '100%', // Ensure video doesn't overflow its container
        }}
      >
        {sources}
      </video>
    </Box>
  );
};

// Set component property types.
VideoPlayer.propTypes = {
  videoFiles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      src: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['video/mp4', 'video/webm']).isRequired,
    })
  ).isRequired,
  responsive: PropTypes.bool,
};

// Set default properties.
VideoPlayer.defaultProps = {
  responsive: false,
};

// Export component.
export default VideoPlayer;
